<script setup>
import { socialShareImage, usePageHead } from '@composables/use-page-head'
import { computed } from 'vue'
import { useAuthStore } from '@stores/auth'
import LatestMixes from '@views/browse-mixes/browse-latest-mixes'
import TrendingMixes from '@views/browse-mixes/browse-trending-mixes'
import RecentlyPlayedMixes from '@views/browse-mixes/browse-recently-played-mixes'

// `<component :is>` resolves a STRING against registered components, never against
// <script setup> bindings — the routes hand this view the name of the tab to render, so the
// name has to be mapped back to the component here.
const MAIN_COMPONENTS = {
    LatestMixes,
    TrendingMixes,
    RecentlyPlayedMixes,
}

const TABS = [
    { to: { name: 'browse/mixes/latest' }, label: 'Latest' },
    { to: { name: 'browse/mixes/trending' }, label: 'Trending' },
]

const AUTH_TABS = [
    ...TABS,
    { to: { name: 'browse/mixes/recently' }, label: 'Recently Played' },
]

const props = defineProps({
    mainComponent: {
        type: String,
        default: 'LatestMixes',
    },
})

const authStore = useAuthStore()

const mainView = computed(() => MAIN_COMPONENTS[props.mainComponent])

const loggedIn = computed(() => authStore.loggedIn)

const tabs = computed(() => (loggedIn.value ? AUTH_TABS : TABS))

usePageHead(computed(() => {
    const isTrending = props.mainComponent === 'TrendingMixes'

    return {
        title: isTrending ? 'Trending Mixes' : 'Latest Mixes',
        description: isTrending
            ? 'The most popular electronic music mixes right now on FRISKY'
            : 'The latest electronic music mixes on FRISKY',
        image: socialShareImage(),
        canonical: isTrending
            ? 'https://frisky.fm/browse/mixes/trending'
            : 'https://frisky.fm/browse/mixes/latest',
    }
}))
</script>

<template>
    <BaseContent
        class="page"
        title="Mixes"
    >
        <BaseTabPanel
            :tabs="tabs"
            class="tabs"
            is-underlined
            is-unpadded
            address-navigation
        />
        <component
            :is="mainView"
            class="component"
        />
    </BaseContent>
</template>

<style lang="scss" scoped>
@import '@design';

.tabs {
    margin-bottom: $s-xxl;
}

.page {
    min-height: 101vh;
}
</style>
